import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Check } from 'lucide-react';
import { SEO } from '@/components/common/SEO';
import { ServiceIconBox } from '@/components/common/ServiceIcon';
import { PageLoader } from '@/components/ui/Spinner';
import { useServices } from '@/hooks/useServices';
import { useTranslation } from '@/features/i18n';

export function ServicesPage() {
  const { data: services, isLoading } = useServices();
  const { t } = useTranslation();

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <SEO
        title={t.services.title}
        description={t.services.seoDescription}
      />

      {/* Hero Section - ninito style */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-[#edf1f5] overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-20">
          <div className="max-w-[800px]">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-block text-sm font-medium text-[#0078ff] uppercase tracking-wider mb-4"
            >
              {t.services.subtitle}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-[40px] md:text-[52px] lg:text-[64px] font-semibold text-black leading-[1.1] tracking-[-0.02em]"
            >
              {t.services.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-6 text-xl text-[#666] leading-relaxed max-w-[600px]"
            >
              {t.services.description}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Services Grid - WHITE cards on GRAY background */}
      <section className="py-20 bg-[#edf1f5]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-8">
            {services?.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link to={`/services/${service.slug}`} className="block h-full group">
                  <div className="bg-white rounded-[28px] p-8 lg:p-10 h-full shadow-[0_4px_24px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_48px_rgba(0,0,0,0.1)] transition-all duration-300">
                    <div className="flex items-start gap-5">
                      <ServiceIconBox icon={service.icon} variant="primary" size="lg" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h2 className="text-2xl font-semibold text-black tracking-tight group-hover:text-[#0078ff] transition-colors">
                            {service.title}
                          </h2>
                          {service.is_featured && (
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#0078ff]/10 text-[#0078ff]">
                              {t.services.popular}
                            </span>
                          )}
                        </div>
                        <p className="mt-3 text-[#666] leading-relaxed">
                          {service.short_description}
                        </p>
                      </div>
                    </div>

                    {service.features_json && service.features_json.length > 0 && (
                      <ul className="mt-8 space-y-3">
                        {service.features_json.slice(0, 4).map((feature, i) => (
                          <li key={i} className="flex items-center text-sm text-[#666]">
                            <div className="w-6 h-6 rounded-lg bg-[#f2f2f2] flex items-center justify-center mr-3 flex-shrink-0">
                              <Check className="w-3.5 h-3.5 text-black" />
                            </div>
                            {feature.title}
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="mt-8 pt-6 border-t border-[#edf1f5] flex items-center justify-between">
                      <span className="text-[#0078ff] font-medium group-hover:text-black transition-colors">
                        {t.services.readMore} {service.title.toLowerCase()}
                      </span>
                      <ArrowUpRight className="w-5 h-5 text-[#0078ff] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - DARK background */}
      <section className="relative bg-black py-32 overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#0078ff]/20 rounded-full blur-[120px]" />
        </div>

        <div className="relative max-w-[1200px] mx-auto px-5 md:px-10 lg:px-20 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-[40px] md:text-[48px] lg:text-[56px] font-semibold text-white tracking-[-0.02em]"
          >
            {t.services.cta.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-6 text-xl text-white/60 max-w-[600px] mx-auto"
          >
            {t.services.cta.description}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-12"
          >
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-black font-medium text-lg hover:bg-white/90 transition-all group"
            >
              {t.services.cta.button}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
